import { Injectable } from '@angular/core';
import { AccountFeatureState } from './account.types';
import * as DatasourceActions from './datasource/datasource.actions';
import * as DatasourceSelectors from './datasource/datasource.selectors';

import { Store } from '@ngrx/store';
import { DatasourceDomain } from '@mode/shared/contract-common';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, first, map, shareReplay } from 'rxjs/operators';
import { DatasourceClientService } from '@mode/shared/data-access-webapp';
import { OrgTypes } from '@mode/shared/contract-common';
@Injectable({
  providedIn: 'root',
})
export class DatasourceFacade {
  constructor(private store: Store<AccountFeatureState>, private service: DatasourceClientService) {
    this.isDatasourcesComplete$ = store.select(DatasourceSelectors.selectIsDatasourcesComplete);
    this.isDatasourcesLoading$ = store.select(DatasourceSelectors.selectIsDatasourcesLoading);
    this.datasources$ = combineLatest([
      store.select(DatasourceSelectors.selectDatasources),
      this.isDatasourcesComplete$,
    ]).pipe(
      filter(([, complete]) => complete),
      map(([datasources]) => datasources)
    );

    this.metricDatasources$ = this.datasources$.pipe(
      map((datasources) => datasources.filter((d) => d.adapter === 'jdbc:dbtmetrics'))
    );

    this.orgDatasources$ = combineLatest([
      store.select(DatasourceSelectors.selectOrgDatasources),
      this.isDatasourcesComplete$,
    ]).pipe(
      filter(([, complete]) => complete),
      map(([datasources]) => datasources)
    );

    this.defaultDatasourceOverride$ = new BehaviorSubject<string | number | null>(null);
    this.defaultDatasource$ = combineLatest([
      this.defaultDatasourceOverride$.pipe(distinctUntilChanged()),
      this.datasources$,
    ]).pipe(
      map(([override, datasources]) => {
        return (
          datasources.find((ds) => ds.token === override || ds.id === override) ||
          datasources.find((ds) => ds.isDefault && !ds.public) ||
          datasources.find((ds) => ds.isDefault) ||
          datasources[datasources.length - 1] || // default to Public warehouse if not available
          null
        );
      }),
      distinctUntilChanged(),
      shareReplay(1)
    );

    this.defaultOrgDatasource$ = combineLatest([this.defaultDatasourceOverride$, this.orgDatasources$]).pipe(
      map(([override, datasources]) => {
        return (
          datasources.find((ds) => ds.token === override || ds.id === override) ||
          datasources.find((ds) => ds.isDefault) ||
          datasources[0] ||
          null
        );
      }),
      distinctUntilChanged(),
      shareReplay(1)
    );

    this.isReportDatasourcesComplete$ = store.select(DatasourceSelectors.selectIsReportDatasourcesComplete);
    this.isReportDatasourcesLoading$ = store.select(DatasourceSelectors.selectIsReportDatasourcesLoading);
    this.reportDatasources$ = combineLatest([
      store.select(DatasourceSelectors.selectReportDatasources),
      this.isReportDatasourcesComplete$,
    ]).pipe(
      filter(([, complete]) => complete),
      map(([datasources]) => datasources)
    );

    this.reportDatasourcesUI$ = store.select(DatasourceSelectors.selectDatasourcesUI);
  }

  /**
   * Running a query sets the user's default datasource for the relevant workspace. The actual data
   * storing the default is in the UserMembership table, but that isn't directly available through
   * the API. While the default on page load will come from the data_sources endpoint, when running
   * a query and such, this can allow temporary setting of the default after running a query since
   * the only other alternative would be to refetch the entire data_sources collection.
   */
  private defaultDatasourceOverride$: BehaviorSubject<string | number | null>;

  /**
   * The data source for the public warehouse
   */
  public datasources$: Observable<ReadonlyArray<DatasourceDomain.Datasource>>;
  public metricDatasources$: Observable<ReadonlyArray<DatasourceDomain.Datasource>>;
  public isDatasourcesComplete$: Observable<boolean>;
  public isDatasourcesLoading$: Observable<boolean>;

  public reportDatasources$: Observable<ReadonlyArray<DatasourceDomain.Datasource>>;
  public isReportDatasourcesComplete$: Observable<boolean>;
  public isReportDatasourcesLoading$: Observable<boolean>;

  /**
   * Datasources for the currently selected workspace. This will not
   * be the report owner's datasources if the user is not a member of
   * that organization. If the user is not a member of a report's workspace,
   * there isn't a way to get those datasources anyway. This excludes the
   * public warehouse.
   *
   * There are some contexts where we do not want the user
   * to accidentally select the public warehouse.
   */
  public orgDatasources$: Observable<ReadonlyArray<DatasourceDomain.Datasource>>;

  /**
   * Default datasource, including the public warehouse. "Default" generally means
   * last ran datasource. Currently used to determine the default datasource for
   * a new query.
   */
  public defaultDatasource$: Observable<DatasourceDomain.Datasource | null>;

  /**
   * The default datasource excluding the public warehouse. Use this when working
   * with `orgDatasources$`. If the public warehouse happened to be the default,
   * then this will return the first private datasource.
   */
  public defaultOrgDatasource$: Observable<DatasourceDomain.Datasource | null>;

  public reportDatasourcesUI$: Observable<{ [token: string]: DatasourceDomain.DatasourceUI | undefined }>;

  /**
   * Loads all the datasources, public and private. This will use the
   * current org in the account facade to retrieve the private datasources. If the
   * current org is public (a user), then it will only retrieve the public warehouse.
   *
   * TODO: Fix action hygiene
   */
  public loadDatasources() {
    this.store
      .select(DatasourceSelectors.selectIsDatasourcesComplete)
      .pipe(
        first(),
        filter((complete) => complete === false)
      )
      .subscribe(() => this.store.dispatch(DatasourceActions.loadDatasources()));
  }

  /**
   * Updates the "default" datasource for the current session. Does not persist
   * this change to the backend. That is done by running a query.
   *
   * The id parameter is `string | number` due to an unfortunate typing mistake on
   * StateRecord, which marks `id` as a string. DatasourceId is really a number, but
   * in a lot of places it's mistakenly typed as a string.
   * @param id The datasource id.
   */
  public updateDefaultDatasource(id: string | number) {
    this.defaultDatasourceOverride$.next(id);
  }

  /**
   * Loads datasources for the current report owner.
   */
  public loadReportDatasources() {
    this.store
      .select(DatasourceSelectors.selectIsReportDatasourcesComplete)
      .pipe(
        first(),
        filter((complete) => complete === false)
      )
      .subscribe(() => this.store.dispatch(DatasourceActions.loadReportDatasources()));
  }

  public refreshDatasource(token: string) {
    this.store.dispatch(DatasourceActions.refreshDatasource({ token }));
  }

  fetchDataSourcesList(orgId: string): Observable<OrgTypes.DataSourcesListResponse> {
    return this.service.fetchDataSourcesList(orgId);
  }

  inviteUser(orgName: string, params: OrgTypes.InviteRequestPayload): Observable<OrgTypes.InviteErrorResponse> {
    return this.service.inviteUser(orgName, params);
  }

  getUserPermission(orgId: string, username: string): Observable<OrgTypes.UserPermission> {
    return this.service.getUserInvitePermission(orgId, username);
  }
}
